import React, { useState, useEffect } from 'react';
import './Paywall.css';

// Check if Stripe is configured (for test mode detection)
const isStripeConfigured = () => {
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  return key && key !== 'pk_test_your_key_here';
};

// Get Lambda function URL from environment or use default
// Lambda Function URLs don't support path routing, so we strip any paths
const getLambdaUrl = () => {
  const url = process.env.REACT_APP_LAMBDA_FUNCTION_URL || 
              process.env.REACT_APP_API_URL || 
              'https://your-lambda-url.execute-api.us-east-1.amazonaws.com/prod';
  
  // Remove any path after the domain (Lambda Function URLs are just the base URL)
  // Also remove trailing slashes
  try {
    const urlObj = new URL(url);
    // Reconstruct URL with just protocol, hostname, and port (no path)
    const cleanUrl = `${urlObj.protocol}//${urlObj.host}${urlObj.port ? ':' + urlObj.port : ''}`;
    return cleanUrl;
  } catch (e) {
    // If URL parsing fails, just remove trailing slashes and any /create-checkout-session path
    return url.replace(/\/+$/, '').replace(/\/create-checkout-session.*$/, '');
  }
};

const Paywall = ({ onUnlock, onClose }) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Check for payment success in URL (after Stripe Checkout redirect)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success');
    
    if (success === 'true' && sessionId) {
      // Payment was successful
      if (onUnlock) {
        onUnlock();
      }
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [onUnlock]);

  const handleCheckout = async () => {
    if (!isStripeConfigured()) {
      // Test mode: Simulate successful payment
      setProcessing(true);
      await new Promise(resolve => setTimeout(resolve, 2000));
      if (onUnlock) {
        onUnlock();
      }
      setProcessing(false);
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const lambdaUrl = getLambdaUrl();
      // Lambda Function URLs don't support path routing - use the URL directly
      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: 2900, // $29.00 in cents
          currency: 'usd',
          successUrl: `${window.location.origin}${window.location.pathname}?success=true&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}${window.location.pathname}?canceled=true`,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText || 'Failed to create checkout session' };
        }
        console.error('Lambda function error:', errorData);
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const data = await response.json();
      console.log('Checkout session response:', data);
      
      const { sessionId, url } = data;

      // Validate response has required fields
      if (!sessionId && !url) {
        console.error('Invalid response format:', data);
        throw new Error('Invalid response from server: missing sessionId and url');
      }

      // Validate the URL format
      if (url && !url.startsWith('https://checkout.stripe.com/')) {
        console.error('Invalid checkout URL format:', url);
        throw new Error('Invalid checkout URL received from server');
      }

      // Redirect to Stripe Checkout using the URL directly
      // Always prefer the URL from the response as it's the official Stripe URL
      if (url) {
        console.log('Redirecting to Stripe Checkout:', url);
        window.location.href = url;
      } else if (sessionId) {
        // Fallback: construct URL from sessionId if URL not provided
        // Note: This format may not always work, prefer using the URL from the API
        console.warn('Using fallback URL construction for session:', sessionId);
        window.location.href = `https://checkout.stripe.com/c/pay/${sessionId}`;
      } else {
        throw new Error('No valid checkout URL or session ID received');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      const errorMessage = err.message || 'Payment failed. Please try again.';
      setError(errorMessage);
      setProcessing(false);
      
      // Show additional help for debugging
      if (errorMessage.includes('Invalid response') || errorMessage.includes('missing')) {
        console.error('Troubleshooting tips:');
        console.error('1. Check that REACT_APP_LAMBDA_FUNCTION_URL is set correctly');
        console.error('2. Verify the Lambda function is deployed and accessible');
        console.error('3. Check Lambda function logs in AWS Console');
        console.error('4. Verify STRIPE_SECRET_KEY is set in Lambda environment variables');
      }
    }
  };

  return (
    <div className="paywall-overlay">
      <div className="paywall-container">
        <button className="paywall-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        
        <div className="paywall-content">
          <div className="paywall-header">
            <h2 className="paywall-title">Unlock Full Cost Breakdown</h2>
            <p className="paywall-subtitle">
              Get detailed insights into your workforce stability costs
            </p>
          </div>

          <div className="paywall-features">
            <div className="paywall-feature">
              <span className="paywall-feature-icon">✓</span>
              <span>Complete cost breakdown analysis</span>
            </div>
            <div className="paywall-feature">
              <span className="paywall-feature-icon">✓</span>
              <span>Detailed financial insights</span>
            </div>
            <div className="paywall-feature">
              <span className="paywall-feature-icon">✓</span>
              <span>Exportable reports</span>
            </div>
          </div>

          <div className="paywall-pricing">
            <div className="paywall-price">
              <span className="paywall-price-amount">$29</span>
              <span className="paywall-price-period">one-time</span>
            </div>
            
            {error && <div className="paywall-error">{error}</div>}
            
            {!isStripeConfigured() && (
              <div className="paywall-test-mode">
                <p className="paywall-test-notice">
                  ⚠️ Test Mode: Stripe key not configured. Payment will be simulated.
                </p>
                <p className="paywall-test-instructions">
                  To enable real payments, set REACT_APP_STRIPE_PUBLISHABLE_KEY in your .env file
                </p>
              </div>
            )}
            
            <button
              className="paywall-btn paywall-btn-primary paywall-btn-large"
              onClick={handleCheckout}
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Unlock Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Paywall;

